import VideoCard from "@/components/VideoCard";
import { Loader2, AlertCircle, Film, LinkIcon, RefreshCw } from "lucide-react";

export default function VideoGrid({ videos, loading, error, selectedUris, completedUris, vaultMap, onToggleSelect, vimeoConnected, dropboxConnected, gdriveConnected, onRefresh }) {
  if (loading) {
    return (
      <div data-testid="loading-state" className="flex flex-col items-center justify-center py-24 gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-[#c6a96f]" />
        <p className="text-sm text-[#71717a]">Loading your footage from the vault...</p>
      </div>
    );
  }

  // Determine the real situation
  const needsVimeo = vimeoConnected === false;  // explicitly false, not undefined
  const needsDestination = dropboxConnected === false && gdriveConnected === false;
  const healthLoaded = vimeoConnected !== undefined;
  const servicesOk = vimeoConnected && (dropboxConnected || gdriveConnected);

  // Error state: services disconnected — show reconnect
  if (healthLoaded && (needsVimeo || needsDestination) && !videos.length) {
    return (
      <div data-testid="error-state" className="flex flex-col items-center justify-center py-20 gap-5">
        <div className="w-16 h-16 rounded-2xl bg-[#1a1d21] border border-[#2a2e36] flex items-center justify-center">
          <LinkIcon className="h-7 w-7 text-[#c6a96f]" />
        </div>
        <div className="text-center max-w-sm">
          <p className="text-base font-medium text-[#f4f4f5] mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {needsVimeo && needsDestination
              ? "Connect Your Accounts"
              : needsVimeo
              ? "Reconnect Vimeo"
              : "Connect a Destination"
            }
          </p>
          <p className="text-sm text-[#a1a1aa] leading-relaxed">
            {needsVimeo
              ? "Your Vimeo connection has expired. Click \"reconnect\" next to Vimeo in the sidebar to re-authorize."
              : "Connect Dropbox or Google Drive in the sidebar to start transferring."
            }
          </p>
        </div>
        <p className="text-[10px] text-[#52525b] font-mono tracking-wider">
          // check the sidebar — look for the "reconnect" link
        </p>
      </div>
    );
  }

  // Error state: services are connected but video fetch failed — show retry
  if (error && !videos.length) {
    return (
      <div data-testid="error-state" className="flex flex-col items-center justify-center py-20 gap-5">
        <div className="w-16 h-16 rounded-2xl bg-[#1a1d21] border border-[#2a2e36] flex items-center justify-center">
          <AlertCircle className="h-7 w-7 text-[#c47272]" />
        </div>
        <div className="text-center max-w-sm">
          <p className="text-base font-medium text-[#f4f4f5] mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Couldn't Load Your Videos
          </p>
          <p className="text-sm text-[#a1a1aa] leading-relaxed">
            Something hiccuped while loading your library. Try refreshing the page — that usually does the trick.
          </p>
        </div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="btn-ghost px-4 py-2 rounded-xl text-xs flex items-center gap-2"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Try Again
          </button>
        )}
        <p className="text-[10px] text-[#52525b] font-mono tracking-wider">
          // if this keeps happening, try reconnecting in the sidebar
        </p>
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div data-testid="empty-state" className="flex flex-col items-center justify-center py-24 gap-4">
        <Film className="h-10 w-10 text-[#3f3f46]" />
        <p className="text-sm text-[#71717a]">No footage found</p>
        <p className="text-xs text-[#52525b] font-mono">// the timeline is empty</p>
      </div>
    );
  }

  return (
    <div data-testid="video-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
      {videos.map((video, i) => (
        <VideoCard
          key={video.uri}
          video={video}
          selected={selectedUris.has(video.uri)}
          inVault={completedUris && completedUris.has(video.uri)}
          vaultDestinations={vaultMap ? vaultMap[video.uri] : null}
          onToggle={() => onToggleSelect(video.uri)}
          delay={i * 40}
        />
      ))}
    </div>
  );
}
